
import React, { useState, useEffect } from 'react';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from '@vercel/speed-insights/react';
import Header from './components/Header';
import Hero from './components/Hero';
import About from './components/About';
import Performance from './components/Performance';
import Pricing from './components/Pricing';
import Contact from './components/Contact';
import FAQ from './components/FAQ';
import CookieConsent from './components/CookieConsent';
import LegalOverlay from './components/LegalOverlay';
import { PricingTier, Language } from './types';
import { translations } from './translations';

const SuccessToast = ({ lang, onClose }: { lang: Language, onClose: () => void }) => {
  const t = translations[lang].toast;
  return (
    <div className="fixed top-24 left-1/2 -translate-x-1/2 z-[200] w-[calc(100%-3rem)] max-w-md animate-in fade-in slide-in-from-top-4 duration-500">
      <div className="bg-sky-500/10 border border-sky-500/20 backdrop-blur-xl rounded-2xl p-6 shadow-[0_20px_50px_-10px_rgba(14,165,233,0.3)] flex items-center gap-5">
        <div className="shrink-0 w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center shadow-lg shadow-sky-500/40">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div className="flex-1">
          <h4 className="text-white font-bold text-lg leading-tight">{t.title}</h4>
          <p className="text-sky-200/70 text-sm mt-1">{t.desc}</p>
        </div>
        <button onClick={onClose} className="p-2 text-sky-400/50 hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const CheckoutModal = ({ 
  tier, 
  lang, 
  onClose, 
  onOpenLegal 
}: { 
  tier: PricingTier; 
  lang: Language; 
  onClose: () => void;
  onOpenLegal: (type: 'terms' | 'disclaimer') => void;
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
  const t = translations[lang].modal;

  const handleCheckout = () => {
    if (!acceptedTerms || !acceptedDisclaimer) return;
    setIsProcessing(true);
    setTimeout(() => {
        window.location.href = tier.paymentLink;
    }, 500);
  };

  const canProceed = acceptedTerms && acceptedDisclaimer && !isProcessing;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl">
      <div className="card-dark max-w-lg w-full p-8 relative border border-white/10 bg-zinc-950 shadow-2xl">
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-500 hover:text-white transition-colors">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>

        <h3 className="text-3xl font-bold mb-8 tracking-tighter text-white text-center">{t.title}</h3>
        
        <div className="bg-white/5 rounded-2xl p-6 mb-8 border border-white/5">
          <div className="flex justify-between items-center mb-4">
            <span className="text-gray-400 text-xs uppercase tracking-widest">{t.product}</span>
            <span className="text-xl font-black text-white">{tier.name}</span>
          </div>
          <div className="flex justify-between items-center border-t border-white/10 pt-4">
            <span className="text-gray-400 text-xs uppercase tracking-widest">{t.total}</span>
            <span className="text-4xl font-black text-sky-400 tracking-tighter">{tier.price}€</span>
          </div>
        </div>

        <div className="space-y-5 mb-10">
          <div className="flex items-start gap-4 group">
            <input 
              type="checkbox" 
              id="terms-checkbox"
              className="mt-1 h-5 w-5 rounded border-white/20 bg-white/5 text-sky-600 focus:ring-sky-500 cursor-pointer"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
            />
            <label htmlFor="terms-checkbox" className="text-sm text-gray-400 leading-snug cursor-pointer select-none">
              {lang === 'es' ? (
                <>Acepto los <button onClick={(e) => { e.preventDefault(); onOpenLegal('terms'); }} className="text-sky-400 hover:text-sky-300 underline underline-offset-2">términos legales y condiciones del servicio.</button></>
              ) : (
                <>I accept the <button onClick={(e) => { e.preventDefault(); onOpenLegal('terms'); }} className="text-sky-400 hover:text-sky-300 underline underline-offset-2">legal terms and service conditions.</button></>
              )}
            </label>
          </div>

          <div className="flex items-start gap-4 group">
            <input 
              type="checkbox" 
              id="disclaimer-checkbox"
              className="mt-1 h-5 w-5 rounded border-white/20 bg-white/5 text-sky-600 focus:ring-sky-500 cursor-pointer"
              checked={acceptedDisclaimer}
              onChange={(e) => setAcceptedDisclaimer(e.target.checked)}
            />
            <label htmlFor="disclaimer-checkbox" className="text-sm text-gray-400 leading-snug cursor-pointer select-none">
              {lang === 'es' ? (
                <>Autorizo el <button onClick={(e) => { e.preventDefault(); onOpenLegal('disclaimer'); }} className="text-sky-400 hover:text-sky-300 underline underline-offset-2">descargo de responsabilidad</button> y entiendo los riesgos.</>
              ) : (
                <>I authorize the <button onClick={(e) => { e.preventDefault(); onOpenLegal('disclaimer'); }} className="text-sky-400 hover:text-sky-300 underline underline-offset-2">disclaimer</button> and understand the risks.</>
              )}
            </label>
          </div>
        </div>

        <button 
          onClick={handleCheckout}
          disabled={!canProceed}
          className={`w-full font-black py-5 rounded-2xl transition-all uppercase tracking-[0.2em] text-center ${
            canProceed 
            ? 'bg-sky-600 hover:bg-sky-500 text-white shadow-xl shadow-sky-600/30' 
            : 'bg-white/5 text-gray-600 cursor-not-allowed border border-white/5'
          }`}
        >
          {isProcessing ? t.redirecting : t.pay}
        </button>

        <p className="mt-6 text-[10px] text-center text-gray-600 uppercase tracking-widest">
            {t.secure}
        </p>
      </div>
    </div>
  );
};

function App() {
  const [lang, setLang] = useState<Language>('es');
  const [selectedTier, setSelectedTier] = useState<PricingTier | null>(null);
  const [activeLegal, setActiveLegal] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Dynamic SEO Metadata Sync
  useEffect(() => {
    // 1. Update Document Title
    document.title = translations[lang].seo.title;
    
    // 2. Update Meta Description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', translations[lang].seo.description);
    }

    // 3. Update HTML Lang Attribute
    document.documentElement.lang = lang;

    // 4. Update Open Graph and Twitter tags (Optional but recommended)
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', translations[lang].seo.title);
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', translations[lang].seo.description);

  }, [lang]);

  useEffect(() => {
    // Detect browser language or saved preference
    const savedLang = localStorage.getItem('axiomity-lang') as Language;
    if (savedLang) {
      setLang(savedLang);
    } else {
      const browserLang = navigator.language.split('-')[0];
      if (browserLang === 'en') setLang('en');
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setShowSuccess(true);
      window.history.replaceState({}, document.title, window.location.pathname);
      setTimeout(() => setShowSuccess(false), 8000);
    }
  }, []);

  const handleSetLang = (newLang: Language) => {
    setLang(newLang);
    localStorage.setItem('axiomity-lang', newLang);
  };

  const handleAddToCart = (tier: PricingTier) => {
    setSelectedTier(tier);
  };

  const getLegalContent = () => {
    const isEs = lang === 'es';
    switch(activeLegal) {
      case 'terms':
        return isEs ? (
          <div className="space-y-8">
            <section>
              <h3 className="text-xl font-bold text-white mb-4">1. Identificación del responsable</h3>
              <div className="bg-white/5 p-6 rounded-xl border border-white/10 space-y-2">
                <p><strong>Bizald OÜ</strong></p>
                <p>Código de registro: 16160835</p>
                <p>Domicilio social: Harju maakond, Tallinn, Kesklinna linnaosa, Tondi tn 18-11, 11313, Estonia</p>
                <p>Correo electrónico: bizald.ou@gmail.com</p>
                <p className="text-xs text-gray-500 mt-4 italic">AxiomityLab es una marca comercial operada por Bizald OÜ. AxiomityFX es un software de trading comercializado bajo dicha marca.</p>
              </div>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">2. Objeto</h3>
              <p>Los presentes Términos de Servicio regulan el acceso, adquisición y uso del software AxiomityFX, consistente en un programa informático de trading automatizado (Expert Advisor) para la plataforma MetaTrader 4. El producto se comercializa bajo licencia anual de uso, no exclusiva, no transferible y revocable.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">3. Naturaleza del producto</h3>
              <p>AxiomityFX es una herramienta tecnológica. <strong>No constituye asesoramiento financiero, no es gestión de carteras, no es un producto de inversión y no ofrece garantías de rentabilidad.</strong> Bizald OÜ no está registrada como entidad financiera ni asesor regulado ante ningún organismo supervisor. El usuario es el único responsable de las decisiones de uso del software y de su capital.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">4. Condiciones de contratación</h3>
              <p>Modalidad: pago único anual, renovable. Tipo de clientes: consumidores finales (B2C) y empresas/profesionales (B2B). Ámbito: internacional. La licencia puede cancelarse para futuras renovaciones en cualquier momento.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">5. Derecho de desistimiento y devoluciones</h3>
              <p>De conformidad con el artículo 103.m de la Directiva 2011/83/UE: El cliente renuncia expresamente al derecho de desistimiento una vez iniciada la ejecución del contenido digital, esto es, tras la activación o instalación del software. <strong>No se admiten devoluciones ni reembolsos una vez activada la licencia.</strong></p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">6. Licencia de uso y limitaciones</h3>
              <p>Queda expresamente prohibido copiar, modificar o distribuir el software, realizar ingeniería inversa o la reventa, sublicencia o uso compartido. El incumplimiento supondrá la revocación inmediata de la licencia, sin derecho a reembolso.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">7. Responsabilidad</h3>
              <p>Bizald OÜ no será responsable por pérdidas económicas, fallos del bróker, plataforma MetaTrader, VPS o conexión, configuraciones incorrectas o uso indebido. El uso del software se realiza bajo la exclusiva responsabilidad del usuario.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">8. Ley aplicable y jurisdicción</h3>
              <p>Estos términos se rigen por el Derecho de Estonia y normativa europea aplicable. Para consumidores de la UE, serán competentes los tribunales del domicilio del consumidor cuando la ley así lo exija.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">9. Aceptación</h3>
              <p>La contratación del software implica la aceptación expresa de estos Términos de Servicio y del Descargo de Responsabilidad.</p>
            </section>
          </div>
        ) : (
          <div className="space-y-8">
            <section>
              <h3 className="text-xl font-bold text-white mb-4">1. Identification of the responsible party</h3>
              <div className="bg-white/5 p-6 rounded-xl border border-white/10 space-y-2">
                <p><strong>Bizald OÜ</strong></p>
                <p>Registration Code: 16160835</p>
                <p>Address: Harju maakond, Tallinn, Kesklinna linnaosa, Tondi tn 18-11, 11313, Estonia</p>
                <p>Email: bizald.ou@gmail.com</p>
                <p className="text-xs text-gray-500 mt-4 italic">AxiomityLab is a trademark operated by Bizald OÜ. AxiomityFX is a trading software marketed under this brand.</p>
              </div>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">2. Purpose</h3>
              <p>These Terms of Service regulate the access, purchase, and use of the AxiomityFX software, consisting of an automated trading program (Expert Advisor) for the MetaTrader 4 platform. The product is marketed under a non-exclusive, non-transferable, and revocable annual use license.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">3. Nature of the Product</h3>
              <p>AxiomityFX is a technological tool. <strong>It does not constitute financial advice, it is not portfolio management, it is not an investment product, and it offers no performance guarantees.</strong> Bizald OÜ is not registered as a financial entity or regulated advisor with any supervisory body. The user is solely responsible for decisions regarding the use of the software and their capital.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">4. Contracting Conditions</h3>
              <p>Modality: single annual payment, renewable. Customer types: end consumers (B2C) and companies/professionals (B2B). Scope: international. The license can be cancelled for future renewals at any time.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">5. Right of Withdrawal and Returns</h3>
              <p>In accordance with Article 103.m of Directive 2011/83/EU: The client expressly waives the right of withdrawal once the execution of digital content has begun, that is, after activation or installation of the software. <strong>No returns or refunds are allowed once the license is activated.</strong></p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">6. License of Use and Limitations</h3>
              <p>It is expressly forbidden to copy, modify, or distribute the software, perform reverse engineering, or resell, sublicense, or share the software. Non-compliance will lead to immediate revocation of the license without the right to a refund.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">7. Liability</h3>
              <p>Bizald OÜ will not be liable for economic losses, broker failures, MetaTrader platform issues, VPS or connection failures, incorrect configurations, or misuse. Use of the software is at the user's sole risk.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">8. Applicable Law and Jurisdiction</h3>
              <p>These terms are governed by the laws of Estonia and applicable European regulations. For EU consumers, the courts of the consumer's domicile will have jurisdiction where required by law.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">9. Acceptance</h3>
              <p>The purchase of the software implies express acceptance of these Terms of Service and the Disclaimer.</p>
            </section>
          </div>
        );
      case 'disclaimer':
        return isEs ? (
          <div className="space-y-8">
            <div className="bg-red-500/10 border border-red-500/20 p-8 rounded-2xl">
              <h3 className="text-2xl font-black text-red-500 uppercase tracking-tighter mb-4">ADVERTENCIA GENERAL DE ALTO RIESGO</h3>
              <p className="text-gray-200 font-medium">El trading en mercados financieros implica alto riesgo y puede suponer la pérdida total del capital invertido. AxiomityFX NO garantiza beneficios, resultados ni estabilidad.</p>
            </div>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">1. Resultados Pasados</h3>
              <p>Los resultados históricos, backtests o simulaciones son meramente orientativos y no garantizan resultados futuros.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">2. Ausencia de Asesoramiento</h3>
              <p>El software no tiene en cuenta la situación financiera del usuario, no constituye recomendación personalizada y no sustituye asesoramiento profesional.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">3. Asunción Total del Riesgo</h3>
              <p>El usuario declara comprender y aceptar que puede perder el 100% del capital, es responsable de la configuración, capital y decisiones, y exime a Bizald OÜ de cualquier reclamación derivada del uso del software.</p>
            </section>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="bg-red-500/10 border border-red-500/20 p-8 rounded-2xl">
              <h3 className="text-2xl font-black text-red-500 uppercase tracking-tighter mb-4">GENERAL HIGH-RISK WARNING</h3>
              <p className="text-gray-200 font-medium">Trading in financial markets involves high risk and may lead to the total loss of invested capital. AxiomityFX does NOT guarantee profits, results, or stability.</p>
            </div>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">1. Past Results</h3>
              <p>Historical results, backtests, or simulations are merely indicative and do not guarantee future results.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">2. Absence of Advice</h3>
              <p>The software does not take the user's financial situation into account, does not constitute a personalized recommendation, and does not replace professional advice.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">3. Full Risk Assumption</h3>
              <p>The user declares that they understand and accept that they may lose 100% of their capital, are responsible for configurations, capital, and decisions, and release Bizald OÜ from any claims arising from the use of the software.</p>
            </section>
          </div>
        );
      case 'privacy':
        return isEs ? (
          <div className="space-y-8">
            <section>
              <h3 className="text-xl font-bold text-white mb-4">1. Responsable del Tratamiento</h3>
              <p>Bizald OÜ. Email: bizald.ou@gmail.com</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">2. Datos Recopilados</h3>
              <p>Datos identificativos, de contacto, de facturación y datos técnicos (IP, navegador).</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">3. Finalidad</h3>
              <p>Gestión de la contratación, soporte técnico, cumplimiento legal y comunicaciones relacionadas con el producto.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">4. Base Legal</h3>
              <p>Ejecución de un contrato, cumplimiento de obligaciones legales y consentimiento del usuario.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">5. Destinatarios</h3>
              <p>No se cederán datos salvo obligación legal o proveedores necesarios para la prestación del servicio.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">6. Derechos</h3>
              <p>El usuario puede ejercer sus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a bizald.ou@gmail.com.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">7. Conservación</h3>
              <p>Los datos se conservarán mientras exista relación contractual y durante los plazos legales exigidos.</p>
            </section>
          </div>
        ) : (
          <div className="space-y-8">
            <section>
              <h3 className="text-xl font-bold text-white mb-4">1. Data Controller</h3>
              <p>Bizald OÜ. Email: bizald.ou@gmail.com</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">2. Collected Data</h3>
              <p>Identifying, contact, billing, and technical data (IP, browser).</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">3. Purpose</h3>
              <p>Contract management, technical support, legal compliance, and communications related to the product.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">4. Legal Basis</h3>
              <p>Execution of a contract, compliance with legal obligations, and user consent.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">5. Recipients</h3>
              <p>No data will be transferred except under legal obligation or to providers necessary for the provision of the service.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">6. Rights</h3>
              <p>The user can exercise their rights of access, rectification, deletion, opposition, limitation, and portability by writing to bizald.ou@gmail.com.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">7. Conservation</h3>
              <p>Data will be kept as long as the contractual relationship exists and during the required legal periods.</p>
            </section>
          </div>
        );
      case 'cookies':
        return isEs ? (
          <div className="space-y-8">
            <section>
              <h3 className="text-xl font-bold text-white mb-4">1. ¿Qué son las cookies?</h3>
              <p>Las cookies son pequeños archivos que se almacenan en el dispositivo del usuario para recordar información sobre su visita.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">2. Tipos de cookies</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Técnicas (necesarias):</strong> Para el funcionamiento básico del sitio.</li>
                <li><strong>Analíticas:</strong> Para entender cómo interactúan los usuarios con el sitio.</li>
                <li><strong>Funcionales:</strong> Para recordar preferencias.</li>
              </ul>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">3. Gestión</h3>
              <p>El usuario puede configurar o rechazar cookies desde su navegador o el banner de consentimiento.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">4. Aceptación</h3>
              <p>Al navegar por el sitio web y aceptar el banner, el usuario consiente el uso de cookies conforme a esta política.</p>
            </section>
          </div>
        ) : (
          <div className="space-y-8">
            <section>
              <h3 className="text-xl font-bold text-white mb-4">1. What are cookies?</h3>
              <p>Cookies are small files stored on the user's device to remember information about their visit.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">2. Types of cookies</h3>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Technical (Necessary):</strong> For basic site functionality.</li>
                <li><strong>Analytical:</strong> To understand how users interact with the site.</li>
                <li><strong>Functional:</strong> To remember preferences.</li>
              </ul>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">3. Management</h3>
              <p>The user can configure or reject cookies from their browser or the consent banner.</p>
            </section>
            <section>
              <h3 className="text-xl font-bold text-white mb-4">4. Acceptance</h3>
              <p>By browsing the website and accepting the banner, the user consents to the use of cookies in accordance with this policy.</p>
            </section>
          </div>
        );
      default: return null;
    }
  };

  const getLegalTitle = () => {
    const t = translations[lang].footer.links;
    switch(activeLegal) {
      case 'terms': return t.terms;
      case 'disclaimer': return t.disclaimer;
      case 'privacy': return t.privacy;
      case 'cookies': return t.cookies;
      default: return '';
    }
  };

  const handleOpenLegal = (type: 'terms' | 'disclaimer') => {
      setActiveLegal(type);
  };

  return (
    <div className="min-h-screen bg-black text-white selection:bg-sky-500 selection:text-white">
      <Header lang={lang} setLang={handleSetLang} />
      
      {showSuccess && <SuccessToast lang={lang} onClose={() => setShowSuccess(false)} />}

      <main>
        <Hero lang={lang} />
        <About lang={lang} />
        <Performance lang={lang} />
        <Pricing lang={lang} onAddToCart={handleAddToCart} />
        <FAQ lang={lang} />
        <Contact lang={lang} />
      </main>

      <CookieConsent lang={lang} onOpenPolicy={() => setActiveLegal('cookies')} />

      {selectedTier && (
        <CheckoutModal 
          tier={selectedTier} 
          lang={lang}
          onClose={() => setSelectedTier(null)} 
          onOpenLegal={handleOpenLegal}
        />
      )}

      <LegalOverlay 
        title={getLegalTitle()} 
        isOpen={activeLegal !== null} 
        onClose={() => setActiveLegal(null)}
        closeLabel={translations[lang].footer.links.close}
      >
        {getLegalContent()}
      </LegalOverlay>

      <footer className="py-20 border-t border-white/10 bg-black">
        <div className="max-w-7xl mx-auto px-6 flex flex-col items-center text-center space-y-12">
          <div className="flex flex-col items-center space-y-4">
            <div className="flex items-center text-4xl font-bold tracking-tight cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
              <span className="text-white">Axiomity</span><span className="text-[#0ea5e9]">Lab</span>
            </div>
          </div>

          <div className="flex items-center gap-6 md:gap-10">
            <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="p-4 bg-white/5 rounded-full border border-white/10 hover:border-sky-500/50 hover:bg-white/10 transition-all group">
              <svg className="w-8 h-8 text-gray-400 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a href="https://tiktok.com" target="_blank" rel="noopener noreferrer" className="p-4 bg-white/5 rounded-full border border-white/10 hover:border-sky-500/50 hover:bg-white/10 transition-all group">
              <svg className="w-8 h-8 text-gray-400 group-hover:text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.89 2.89 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z"/>
              </svg>
            </a>
            <a href="mailto:info@axiomitylab.com" className="p-4 bg-white/5 rounded-full border border-white/10 hover:border-sky-500/50 hover:bg-white/10 transition-all group">
              <svg className="w-8 h-8 text-gray-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </a>
          </div>

          <div className="w-full flex flex-col items-center">
            <h4 className="font-bold mb-8 text-sm uppercase tracking-[0.4em] text-gray-500">{translations[lang].footer.legalInfo}</h4>
            <ul className="flex flex-wrap justify-center gap-x-12 gap-y-6 text-base font-medium text-gray-500">
              <li><button onClick={() => setActiveLegal('terms')} className="hover:text-white transition-colors">{translations[lang].footer.links.terms}</button></li>
              <li><button onClick={() => setActiveLegal('disclaimer')} className="hover:text-white transition-colors">{translations[lang].footer.links.disclaimer}</button></li>
              <li><button onClick={() => setActiveLegal('privacy')} className="hover:text-white transition-colors">{translations[lang].footer.links.privacy}</button></li>
              <li><button onClick={() => setActiveLegal('cookies')} className="hover:text-white transition-colors">{translations[lang].footer.links.cookies}</button></li>
            </ul>
          </div>

          <div className="pt-8 border-t border-white/5 w-full flex flex-col items-center gap-4">
            <p className="text-xs text-gray-600 uppercase tracking-widest font-black">{translations[lang].footer.copyright}</p>
            <p className="text-[11px] text-gray-700 max-w-2xl leading-relaxed">{translations[lang].footer.disclaimer}</p>
          </div>
        </div>
      </footer>
      <Analytics />
      <SpeedInsights />
    </div>
  );
}

export default App;
